import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { StorefrontGrid } from '@components/storefront/StorefrontGrid/StorefrontGrid';
import {
  BackgroundGradient,
  BannerColumn,
  BannerColumns,
  BannerImageColumn,
  BannerInnerBox,
  BannerOuterBox,
  CardTypeLink,
  CategoriesMenu,
  CategoriesMenuColumnsWrapper,
  CategoriesMenuItem,
  CategoriesSelect,
  CategoriesSelectColumnsWrapper,
  ECardsMenusSpacingStack,
  HeroImage,
  HeroImageWrapper,
  PageContentWrapper,
  PrintOnlineMenu,
  PrintOnlineMenuItem,
  StyledReactPaginate,
} from '@components/storefront/ECardsStorefrontPageContent/ECardsStorefrontPageContent.styles';
import { eCardsStorefrontPageContentData } from '@data-ts/components/storefront/ECardsStorefrontPageContent';
import { Column, Columns } from '@components/Columns';
import queryString from 'query-string';
import { ECardCategorySlug, eCardCategorySlugs, getECardsCategoryRoute, getECardTagsByCategory } from '@utils/storefront.util';
import { useIsMounted } from '@hooks/useIsMounted';
import { SelectProps } from '@components/Select/Select';
import { TextPairing } from '@components/TextPairing';
import { cloudAssetUrl } from '@utils/cloudAssetUrl.util';
import { useTheme } from '@styledComponents';
import { defaultBase64Placeholder } from '@components/CloudImage';
import { Text } from '@components/typography';
import { CTALink } from '@components/CTALink';
import { Flex } from '@components/Flex';
import { snakeToCamel } from '@utils/strings.util';
import { PopupInstance, PopupOptions } from '@typeform/embed';
import ScrollMagic from 'scrollmagic';
import { gsap, Linear } from 'gsap';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
// import ReactPaginate from 'react-paginate';
import { useThemeMediaQuery } from '@hooks/useThemeMediaQuery';
import { Button } from '@components/Button';

const TOTAL_ITEM_PER_PAGE: number = 30;

let typeformEmbed: { makePopup: (url: string, options?: PopupOptions) => PopupInstance } | undefined;

if (typeof window !== 'undefined') {
  (async () => {
    typeformEmbed = await import('@typeform/embed');
  })();
}

interface Props {
  productType: ShopifyProductType;
  cards: ReadonlyArray<StorefrontECard>;
  initialCategorySlug?: ECardCategorySlug;
}

interface PaginationSelectedItem {
  selected: number;
}

const copyData = eCardsStorefrontPageContentData;
const getFilteredCards = (cards: ReadonlyArray<StorefrontECard>, selectedCategorySlug?: ECardCategorySlug, hasPrints: boolean = false) => {
  const selectedCategoryTags: ReadonlyArray<ShopifyProductTag> =
    (selectedCategorySlug && getECardTagsByCategory(selectedCategorySlug)) || [];
  const filteredCards = (cards || []).filter(
    card =>
      (!hasPrints || card.tags.includes('Matching Prints')) &&
      (selectedCategoryTags.length === 0 || selectedCategoryTags.some(selectedTag => card.tags.includes(selectedTag)))
  );
  return filteredCards;
};

const heroImagesMap: { [key in ShopifyProductType]: string } = {
  Invitation: 'ecards/invite_hero.png',
  'Save the Date': 'ecards/std_hero.png',
};

export const ECardsStorefrontPageContent: React.FC<Props> = ({ productType, cards, initialCategorySlug }) => {
  const isMobile = useThemeMediaQuery('below_sm');
  const initialHasPrints = queryString.parse(typeof window === 'undefined' ? '' : window.location.search).hasPrints;
  const [hasPrints, setHasPrints] = useState(!!initialHasPrints);
  const [selectedPage, setSelectedPage] = useState(1);
  const [selectedCategorySlug, setSelectedCategorySlug] = useState(initialCategorySlug);
  const [filteredCards, setFilteredCards] = useState(() => {
    return getFilteredCards(cards, initialCategorySlug);
  });
  const scrollController = useScrollController();
  const theme = useTheme();
  const isMounted = useIsMounted();
  useEffect(() => {
    setFilteredCards(getFilteredCards(cards, selectedCategorySlug, hasPrints));
  }, [cards, selectedCategorySlug, hasPrints, setFilteredCards, setSelectedCategorySlug]);
  const updateUrlForFilter = (categorySlug: ECardCategorySlug | undefined, hasPrints: boolean) => {
    const parsedSearch = queryString.parse(window.location.search);
    if (hasPrints) {
      parsedSearch.hasPrints = `${hasPrints}`;
    } else {
      delete parsedSearch.hasPrints;
    }
    const query = queryString.stringify(parsedSearch);
    history.pushState(null, '', `${getECardsCategoryRoute(productType, categorySlug)}${query && `?${query}`}`);
  };
  let bannerEyebrow: string = copyData.invitationsBannerEyebrow;
  let bannerTitle: string = copyData.invitationsBannerTitle;
  let bannerBody: string = copyData.invitationsBannerBody;
  let productImageLabel: string = copyData.eCardImageAltSuffix[productType];
  if (productType === 'Save the Date') {
    bannerEyebrow = copyData.stdBannerEyebrow;
    bannerTitle = copyData.stdBannerTitle;
    bannerBody = copyData.stdBannerBody;
  }
  const heroImageSrc = heroImagesMap[productType];
  const sectionRef = useRef<HTMLDivElement>(null);
  const bgGradientRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    let scene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    const sectionEl = sectionRef.current;
    const bgGradientEl = bgGradientRef.current;
    let sectionTimeline: ReturnType<typeof gsap.timeline> | null = null;
    //
    const bgColorFrom = theme.colors.neutra1;
    const bgColorTo = 'rgba(249, 245, 240, 0)';
    if (scrollController && isMounted && sectionEl) {
      if (scrollController) {
        sectionTimeline = gsap.timeline({ paused: true, defaults: { ease: Linear.easeNone } });
        sectionTimeline.fromTo(
          [bgGradientEl],
          { background: `linear-gradient(to bottom, ${bgColorFrom} 47.95%, ${bgColorTo} 70.28%)` },
          {
            background: `linear-gradient(to bottom, ${bgColorTo} 47.95%, ${bgColorTo} 70.28%)`,
            duration: 1,
          },
          0
        );
      }
    }
    const initScene = () => {
      if (sectionEl && scrollController) {
        scene = new ScrollMagic.Scene({
          triggerElement: sectionEl,
          triggerHook: 'onLeave',
          duration: 450,
        })
          // .addIndicators()
          .addTo(scrollController)
          // workaround from here https://github.com/janpaepke/ScrollMagic/issues/918 until plugin for gsap 3 released https://github.com/janpaepke/ScrollMagic/pull/920 or we update it locally
          .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
            sectionTimeline?.progress(event.progress);
          });
      }
    };
    initScene();
    //
  }, [scrollController, isMounted, theme]);

  return (
    <PageContentWrapper as="section" ref={sectionRef}>
      <BackgroundGradient ref={bgGradientRef} />
      <BannerOuterBox paddingX={[6, 9]} marginBottom={[9, 10]}>
        <BannerInnerBox>
          <BannerColumns verticalAlign={'center'}>
            <BannerColumn width={12} desktop={5}>
              <TextPairing
                alignment={['center', 'center', 'left']}
                eyebrow={bannerEyebrow}
                title={bannerTitle}
                body={bannerBody}
                eyebrowColor="humana4"
                titleColor="neutra6"
                bodyColor="neutra6"
                eyebrowVariant="sectionEyeBrow24"
                titleVariant="hed72Hero"
                bodyVariant="dek20"
                bodyMaxWidth="382px"
                marginBottom={6}
              />
              {productType === 'Invitation' ? (
                <CardTypeLink id="ecardsStoreToStdDesigns" to="/save-the-date/designs/">
                  {copyData.linkToSaveTheDates}
                </CardTypeLink>
              ) : (
                <CardTypeLink id="ecardsStoreToInvitationDesigns" to="/wedding-invitations/designs/" variant="humanaDarkLink">
                  {copyData.linkToInvitations}
                </CardTypeLink>
              )}
            </BannerColumn>
            <BannerImageColumn>
              <HeroImageWrapper>
                <HeroImage
                  srcSet={`${defaultBase64Placeholder} 1w,
             ${cloudAssetUrl(heroImageSrc)} 1430w`}
                  sizes={`(max-width: ${theme.breakpoints[1]}) 1px, 1430px`}
                  src={cloudAssetUrl(heroImageSrc)}
                  alt={productImageLabel + ' example'}
                />
              </HeroImageWrapper>
            </BannerImageColumn>
          </BannerColumns>
        </BannerInnerBox>
      </BannerOuterBox>
      <ECardsMenusSpacingStack>
        <PrintOnlineMenu key={`print-digital-menu-${isMounted}`} isRightSelected={hasPrints} isMounted={isMounted}>
          <PrintOnlineMenuItem
            id="ecardsStoreOnlineFilter"
            href="#"
            $isActive={!hasPrints}
            onClick={event => {
              event.preventDefault();
              setHasPrints(false);
              updateUrlForFilter(selectedCategorySlug, false);
              setSelectedPage(1);
            }}
          >
            {copyData.online}
          </PrintOnlineMenuItem>
          <PrintOnlineMenuItem
            id="ecardsStorePrintFilter"
            href="#"
            $isActive={hasPrints}
            onClick={event => {
              event.preventDefault();
              setHasPrints(true);
              updateUrlForFilter(selectedCategorySlug, true);
              setSelectedPage(1);
            }}
          >
            {copyData.printNOnline}
          </PrintOnlineMenuItem>
        </PrintOnlineMenu>
        <CategoriesSelectColumnsWrapper>
          <Columns>
            <Column width={12}>
              <CategoriesSelect<React.FC<SelectProps<ECardCategorySlug>>>
                value={selectedCategorySlug || ''}
                options={eCardCategorySlugs}
                labelFunction={categorySlug => copyData.categories[categorySlug]}
                onChange={({ target: { value } }: React.ChangeEvent<HTMLSelectElement>) => {
                  let newCategorySlug;
                  if ((eCardCategorySlugs as ReadonlyArray<string>).includes(value)) {
                    newCategorySlug = value as ECardCategorySlug;
                  }
                  setSelectedCategorySlug(newCategorySlug);
                  updateUrlForFilter(newCategorySlug, hasPrints);
                }}
              />
            </Column>
          </Columns>
        </CategoriesSelectColumnsWrapper>
        <CategoriesMenuColumnsWrapper>
          <Columns>
            <Column>
              <CategoriesMenu>
                {eCardCategorySlugs.map(categorySlug => (
                  <CategoriesMenuItem
                    key={categorySlug}
                    id={snakeToCamel(`ecardsStoreCategory-${categorySlug}`)}
                    href={getECardsCategoryRoute(productType, categorySlug)}
                    isSelected={categorySlug === selectedCategorySlug}
                    onClick={event => {
                      event.preventDefault();
                      let newCategorySlug;
                      if (categorySlug !== selectedCategorySlug) {
                        newCategorySlug = categorySlug;
                      }
                      setSelectedCategorySlug(newCategorySlug);
                      updateUrlForFilter(newCategorySlug, hasPrints);
                    }}
                    typography="featureCopy17"
                  >
                    {copyData.categories[categorySlug]}
                  </CategoriesMenuItem>
                ))}
              </CategoriesMenu>
            </Column>
          </Columns>
        </CategoriesMenuColumnsWrapper>
      </ECardsMenusSpacingStack>
      <StorefrontGrid cards={filteredCards.slice(0, selectedPage * TOTAL_ITEM_PER_PAGE)} />
      <StyledReactPaginate isDisabled={filteredCards.length < TOTAL_ITEM_PER_PAGE * selectedPage} marginTop={[5]}>
        {/*
        <ReactPaginate
          breakLabel={'...'}
          pageCount={filteredCards.length < TOTAL_ITEM_PER_PAGE ? 0 : Math.ceil(filteredCards.length / TOTAL_ITEM_PER_PAGE)}
          marginPagesDisplayed={isMobile ? 1 : 2}
          pageRangeDisplayed={isMobile ? 3 : 5}
          onPageChange={(selectedItem: PaginationSelectedItem) => {
            window.scrollTo({ top: 600, behavior: 'smooth' });
            setSelectedPage(selectedItem.selected);
          }}
          containerClassName={'pagination'}
          activeClassName={'active'}
        />
        */}
        <Button id={'load-more-button'} variant="secondary" onClick={() => setSelectedPage(selectedPage + 1)}>
          {copyData.loadMoreButton}
        </Button>
      </StyledReactPaginate>
      <Flex alignItems="center" flexDirection="column" marginTop={[9, 10]}>
        <Text tagName="p" variant="featureHed17" color="neutra5">
          {copyData.notFindingWhatLooking}
        </Text>
        <CTALink
          id="ecardsStoreLookingForMore"
          href="#"
          onClick={e => {
            e.preventDefault();
            typeformEmbed!.makePopup('https://form.typeform.com/to/lIVZl3rB?typeform-medium=embed-snippet', { mode: 'popup' }).open();
          }}
        >
          {copyData.letUsKnow}
        </CTALink>
      </Flex>
    </PageContentWrapper>
  );
};
